import type { AiAction, AiMonthlyUsage, AiSpendStatus } from "../types";

/** Spend as a percentage of the cap (may exceed 100); 0 when the cap is not positive. */
export const spendPercent = (spentUsd: number, capUsd: number): number =>
  capUsd > 0 ? Math.round((spentUsd / capUsd) * 100) : 0;

/** True once month-to-date spend has reached (>=) the cap: new AI requests are paused. */
export const isCapReached = (
  status: Pick<AiSpendStatus, "spentUsd" | "capUsd">,
): boolean => status.spentUsd >= status.capUsd;

export type AiActionTotals = {
  action: AiAction;
  calls: number;
  tokens: number;
};

/** Calls and tokens (input + output) per action for one month of the rollup. */
export const totalsByAction = (
  rollup: readonly AiMonthlyUsage[],
  month: string,
): AiActionTotals[] =>
  rollup
    .filter((row) => row.month === month)
    .map((row) => ({
      action: row.action,
      calls: row.calls,
      tokens: Number(row.input_tokens) + Number(row.output_tokens),
    }));

export type AiMonthTotals = {
  month: string;
  calls: number;
  tokens: number;
  costUsd: number;
};

/** Month-by-month history (newest first), summed over actions. */
export const monthHistory = (
  rollup: readonly AiMonthlyUsage[],
): AiMonthTotals[] => {
  const byMonth = new Map<string, AiMonthTotals>();
  for (const row of rollup) {
    const prev = byMonth.get(row.month) ?? {
      month: row.month,
      calls: 0,
      tokens: 0,
      costUsd: 0,
    };
    byMonth.set(row.month, {
      month: row.month,
      calls: prev.calls + row.calls,
      tokens:
        prev.tokens + Number(row.input_tokens) + Number(row.output_tokens),
      costUsd: prev.costUsd + Number(row.estimated_cost_usd),
    });
  }
  return [...byMonth.values()].sort((a, b) => b.month.localeCompare(a.month));
};

/**
 * Parses the cap text typed by direccion; returns the positive finite number
 * or `null` when invalid (mirrors `ai_settings_monthly_cap_usd_check`).
 */
export const parseCapInput = (text: string): number | null => {
  const normalized = text.trim().replace(",", ".");
  if (normalized === "") return null;
  const value = Number(normalized);
  return Number.isFinite(value) && value > 0 ? value : null;
};
